// The left column of «Почта» (R-M6) in the frame's standard column (um/2026-10-30/03 п.1): the folders with unread counts,
// the mailboxes with their sync state, then the folders of the section (letters the system sends, templates, mailings) as the plain
// tree. On those pages the column stays as it is: the mailboxes do not go away, the page opens in the body.

import { useMemo } from 'react';
import type { Id, MenuSection } from '@confdeck/core-shared';
import { navigate, useLocation } from '../../shell/router.ts';
import { Branches, NavRow, SideNav, useFolder } from '../../shell/SideNav.tsx';
import type { NavActive } from '../../shell/nav-tree.ts';
import { Button } from '../../ui/primitives.tsx';
import { FOLDER_LIST, inView, type View } from './model.ts';
import { setMailNav, useMailNav } from './nav-state.ts';
import { useLetters, useMailboxes } from './use-letters.ts';
import sh from '../../shell/shell.module.css';
import s from './mail.module.css';

const onMail = (path: string) => path === '/s/mail';

export function MailNav({ section, active }: { section: MenuSection; active: NavActive }) {
  const letters = useLetters();
  const boxes = useMailboxes();
  const { box, view } = useMailNav();
  const { path } = useLocation();
  const here = onMail(path);
  const [boxesOpen, flipBoxes] = useFolder('mail:boxes');
  const counts = useMemo(() => {
    const c = new Map<View, number>();
    for (const l of letters) {
      if (box !== 'all' && l.mailboxId !== box) continue;
      // Inbox and spam count unread letters, drafts count all of them (HomeMail).
      for (const f of FOLDER_LIST) if (inView(l, f.key) && (f.key === 'drafts' || (l.unread && f.key !== 'sent' && f.key !== 'trash'))) c.set(f.key, (c.get(f.key) ?? 0) + 1);
    }
    return c;
  }, [letters, box]);
  const unreadOf = (id: Id) => letters.filter((l) => l.mailboxId === id && l.unread && l.folder === 'inbox').length;
  // From a page of the section the folders lead back to the letters; the choice is the screen's shared state.
  const go = (change: Parameters<typeof setMailNav>[0]) => { setMailNav(change); if (!here) navigate('/s/mail'); };
  const count = (n: number | undefined) => (n ? <span className={sh.navCount}>{n}</span> : null);

  return (
    <>
      {FOLDER_LIST.map((f) => (
        <NavRow key={f.key} icon={f.icon} label={f.label} on={here && view === f.key} count={count(counts.get(f.key))} onClick={() => go({ view: f.key })} />
      ))}
      <div className={sh.navNode}>
        <NavRow icon="inbox" label="Ящики" onClick={flipBoxes} toggle={{ open: boxesOpen, onToggle: flipBoxes, label: 'Ящики' }} />
        {boxesOpen && (
          <Branches>
            {boxes.length > 1 && <NavRow label="Вся почта" on={here && box === 'all'} onClick={() => go({ box: 'all' })} />}
            {boxes.map((b) => (
              <NavRow key={b.id} title={b.address} on={here && box === b.id} onClick={() => go({ box: b.id })} count={count(unreadOf(b.id))}
                label={<><span className={s.boxDot} data-state={b.kind === 'demo' ? 'demo' : b.syncState === 'error' ? 'error' : b.syncError ? 'warn' : 'ok'} />{b.name}</>}
                action={b.kind === 'imap' ? { icon: 'gear', label: `Настроить ящик ${b.name}`, onClick: () => go({ ask: { kind: 'connect', id: b.id } }) } : undefined} />
            ))}
            <NavRow label="Подключить ящик" muted onClick={() => go({ ask: { kind: 'connect', id: null } })} />
          </Branches>
        )}
      </div>
      <SideNav section={section} active={active} hideScreens />
    </>
  );
}

/** The head of the column on the letters: the folder the person is in, and the mailbox when one is chosen. */
export function useMailTitle(): string | null {
  const { box, view } = useMailNav();
  const boxes = useMailboxes();
  const { path } = useLocation();
  if (!onMail(path)) return null;
  const folder = FOLDER_LIST.find((f) => f.key === view)?.label ?? 'Почта';
  const name = box === 'all' ? null : boxes.find((b) => b.id === box)?.name;
  return name ? `${folder} · ${name}` : folder;
}

/**
 * «Новое письмо» at the bottom of the column (um/2026-10-31/02): the slot where other sections keep the event's plate. On a page of
 * the section (templates, mailings) it leads to the letters and opens the window there.
 */
export function MailFoot() {
  const { path } = useLocation();
  return (
    <Button variant="primary" icon="compose" className={s.composeBtn} onClick={() => { setMailNav({ ask: { kind: 'compose' } }); if (!onMail(path)) navigate('/s/mail'); }}>
      Новое письмо
    </Button>
  );
}
